// FEAT-050 S3 (#267): SMARTDent N단계 재귀 할당 코어 (순수 함수 모듈)
//
// 데이터 모델(Spec v0.3 §6.2b/§7) = 이중 그래프:
//   - 할당 계층: node.allocatedBy (1차 데이터, "누가 이 노드에게 할당했는가")
//   - 딜러 pool 멤버십: parentOf (org 트리, "조직 계층상 부모가 누구인가")
//
// explicit node = nodesByCompanyId 에 자기 t/i/u 레코드를 보유한 노드.
// pool participant = 레코드가 없는 노드. 가장 가까운 explicit 조상(effective owner,
// parentOf 기준으로 산출)의 budget 을 공유 소비한다.

export const isExplicitNode = (companyId, nodesByCompanyId) =>
    !!(companyId && nodesByCompanyId && Object.prototype.hasOwnProperty.call(nodesByCompanyId, companyId));

export const getDirectAllocationChildren = (companyId, nodesByCompanyId) => {
    if (!companyId) return []; // falsy companyId 가 allocatedBy:null(최상위 explicit)과 오매치되는 것 방지
    // IMP-420 m1: base 부재로 병합 노드의 allocatedBy 가 자기 companyId 로 남는 드리프트 케이스 방지 — 자기참조 배제.
    return Object.values(nodesByCompanyId || {}).filter((n) => n && n.allocatedBy === companyId && n.companyId !== companyId);
};

export const resolveEffectiveOwner = (companyId, { nodesByCompanyId, parentOf } = {}) => {
    if (!companyId) return null;

    if (isExplicitNode(companyId, nodesByCompanyId)) {
        return companyId;
    }

    const visited = new Set();
    let current = parentOf ? parentOf[companyId] : undefined;

    while (current && !visited.has(current)) {
        if (isExplicitNode(current, nodesByCompanyId)) {
            return current;
        }
        visited.add(current);
        current = parentOf ? parentOf[current] : undefined;
    }

    return null;
};

// BUG-206 (#369, 사용자 확정 2026-07-31): `issuable`(t − i − Σchild.t) 필드 **제거**.
// `i`(발행 바우처 수)는 리딤 전까지 무한 재발행 가능한 참고치라 quota 를 결정할 수 없다
// (t=1 이어도 리딤 전에는 i 를 무한히 올릴 수 있다). 남겨두면 "발급 가능량" 으로 오선택되는
// 함정이 되므로 공식 자체를 없앤다 — 발급·할당 천장은 `selfUsable`(t − u − Σchild.t) 뿐이다.
const computeQuotaForType = (node, directExplicitChildren, suffix) => {
    const t = (node && node[`t_${suffix}`]) || 0;
    const u = (node && node[`u_${suffix}`]) || 0;
    const sumChildT = (directExplicitChildren || []).reduce(
        (sum, child) => sum + ((child && child[`t_${suffix}`]) || 0),
        0
    );

    return {
        allocatable: t - sumChildT,
        remaining: t - u,
        revokable: t - u,
        // IMP-386: 자기 여유분 = t − u − Σ(child.t). 위임분·자기 redeem 모두 차감한
        // "직접 쓰거나 배분 가능" 순수 잔여. smartDentQuota.computeCorpQuotaBreakdown().allocatable(t−Σd) 과 유사하며
        // Math.max(0) 로 over-commit(u+Σchild>t) 음수 방어(동일 clamp 정책). S5 전환 시
        // Overview self-usable 소비처는 remaining(t−u) 이 아닌 이 키를 사용해야 함(Σd 차감 회귀 방지).
        selfUsable: Math.max(0, t - u - sumChildT),
    };
};

export const computeNodeQuota = (node, directExplicitChildren) => ({
    initial: computeQuotaForType(node, directExplicitChildren, 'initial'),
    renewal: computeQuotaForType(node, directExplicitChildren, 'renewal'),
});

// IMP-427 (#368): 서브트리 리딤 합산 — 선택 조직 + 그 아래 모든 조직의 `u` 합.
//
// 배경: 법인 탭은 자기 corp 원장(2-seg)의 `u` 가 BE 가 유지하는 롤업이라 그대로 쓸 수 있지만,
// 딜러 탭에서 **하위 조직을 선택**하면 그 조직의 레코드(3-seg) `u` 는 자기 리딤만 담는다.
// 따라서 조직 전체 값은 서브트리를 직접 합산해야 한다.
//
// 합산 대상이 3-seg 자기 리딤이라 이중 계상이 없다(2-seg corp 원장의 롤업 u 는
// allocationContext.buildNodesByCompanyId 가 이미 버린다). `allocatedBy` 링크를 BFS 로 따라가며
// visited 로 순환을 방어한다(GetAllocationSubtree 가 순환 데이터를 반환하는 케이스 대비).
export const sumSubtreeRedeemed = (companyId, nodesByCompanyId) => {
    if (!companyId || !nodesByCompanyId) return { newQty: 0, renewQty: 0 };
    const own = nodesByCompanyId[companyId];
    let newQty = (own && own.u_initial) || 0;
    let renewQty = (own && own.u_renewal) || 0;

    const visited = new Set([companyId]);
    const queue = getDirectAllocationChildren(companyId, nodesByCompanyId);
    while (queue.length) {
        const node = queue.shift();
        const id = node && node.companyId;
        if (!id || visited.has(id)) continue;
        visited.add(id);
        newQty += node.u_initial || 0;
        renewQty += node.u_renewal || 0;
        queue.push(...getDirectAllocationChildren(id, nodesByCompanyId));
    }
    return { newQty, renewQty };
};

// IMP-427: 선택 조직이 하위 조직을 가지는지 — 하위가 없으면 서브트리 합 == 자기 리딤이라
// "조직 전체" 표기가 중복 정보가 되므로 호출처가 이 값으로 노출을 결정한다.
export const hasAllocationDescendants = (companyId, nodesByCompanyId) =>
    getDirectAllocationChildren(companyId, nodesByCompanyId).length > 0;

export const getPoolParticipants = (ownerCompanyId, { childrenOf, nodesByCompanyId } = {}) => {
    if (!ownerCompanyId || !childrenOf) return [];

    const result = [];
    const visited = new Set();
    const queue = [...(childrenOf[ownerCompanyId] || [])];

    while (queue.length) {
        const current = queue.shift();
        if (!current || visited.has(current)) continue;
        visited.add(current);

        if (isExplicitNode(current, nodesByCompanyId)) {
            // 자손이 explicit 이면 그 노드+서브트리는 자기 pool 소유자이므로 제외
            continue;
        }

        result.push(current);
        queue.push(...(childrenOf[current] || []));
    }

    return result;
};
